// Implements the gtag.js wrapper convention:
//
//   function gtag(){ dataLayer.push(arguments); }
//
// The standard `gtag` snippet pushes an Arguments object onto dataLayer
// (not a plain Array), because GA's reader inspects command verbs in
// arguments[0] (`'set'`, `'event'`, `'consent'`, …). Modern GTM tolerates
// either shape, but matching the Arguments form keeps us interoperable
// with anything that listens specifically for it (including the consent
// signals consentmanager already pushes — verified in our dataLayer
// trace).
//
// Use this for command-verb pushes:
//   gtag('set', 'user_properties', { sso_id, user_type, user_language })
//   gtag('event', 'login', { method: 'fusionauth' })
//
// For named custom events that GTM listens to as triggers, use
// `pushDataLayer` (event-shaped object push).

// `dataLayer` is already declared as `Object[]` by @next/third-parties
// (node_modules/@next/third-parties/dist/types/google.d.ts). Augmenting
// with a different type triggers TS2717 "subsequent declarations must
// have the same type". We could redeclare to match but it's not worth
// the maintenance — just cast at the use site.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function gtag(...args: any[]): void {
  if (typeof window === 'undefined') return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as unknown as { dataLayer?: any[] };
  if (!w.dataLayer) {
    w.dataLayer = [];
  }
  // Wrap via an inner function so `push` receives an Arguments instance
  // identical to what `function gtag(){dataLayer.push(arguments)}` would
  // produce. Plain `.push(args)` would push an Array, which GA's older
  // reader code may not recognise as a command tuple.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, prefer-spread
  (function thunk() {
    // eslint-disable-next-line prefer-rest-params, @typescript-eslint/no-non-null-assertion
    w.dataLayer!.push(arguments);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }).apply(null, args as any);
}
